import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import io from "socket.io-client";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { calculateCombinedReservationDuration } from "../../utils/bookingDuration";
import { getMexicoDateTime, getMexicoNow } from "../../utils/mexicoTime";
import { decodeBookingGroups } from "../../utils/bookingPlan";
import { API_BASE, SOCKET_URL } from "../../config/api";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import GuestBookingDialog from "../../components/GuestBookingDialog";
import "dayjs/locale/es";

const isValidDayjsDate = (value) =>
  Boolean(value && typeof value.isValid === "function" && value.isValid());

const SLOT_GRID_STYLES = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    sm: "repeat(3, minmax(0, 1fr))",
    md: "repeat(4, minmax(0, 1fr))",
  },
  gap: 1.5,
  alignItems: "stretch",
};

const SLOT_BUTTON_STYLES = {
  minHeight: 48,
  borderRadius: 2.5,
  fontWeight: 750,
  fontSize: { xs: "0.95rem", md: "1rem" },
  px: 1,
  py: 1.25,
  whiteSpace: "nowrap",
  textTransform: "none",
};

const SUMMARY_CARD_STYLES = {
  p: 2,
  borderRadius: 2.5,
  border: "1px solid #eeeae4",
  backgroundColor: "#faf9f7",
};

const money = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 0,
});

const ReservaPage = () => {
  const { serviceId, employeeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const encodedBookingGroups = searchParams.get("bookingGroups");
  const compactSelections = searchParams.get("comboSelections") || `${serviceId}:${employeeId}`;
  const rescheduleBookingId = searchParams.get("rescheduleBookingId");
  const preselectedDate = searchParams.get("selectedDate");
  const preselectedTime = searchParams.get("selectedTime");
  const preselectedDuration = Number(searchParams.get("selectedDuration") || 0);
  const fromCart = searchParams.get("fromCart") === "1";
  const isReschedule = Boolean(rescheduleBookingId);
  const { clearCart } = useCart();
  const { user, loading: authLoading, logout } = useAuth();
  const [guestContact, setGuestContact] = useState(null);
  const [guestContactOpen, setGuestContactOpen] = useState(false);
  const legacySelections = compactSelections
    .split(",")
    .map((item) => {
      const [currentServiceId, currentEmployeeId] = item.split(":");
      return {
        serviceId: Number(currentServiceId),
        employeeId: Number(currentEmployeeId),
      };
    })
    .filter((selection) => selection.serviceId && selection.employeeId);
  const initialBookingGroups = (() => {
    if (!isReschedule && encodedBookingGroups) {
      const decodedGroups = decodeBookingGroups(encodedBookingGroups);
      if (decodedGroups.length) {
        return decodedGroups.map((group) => ({
          id: group.id,
          selections: group.selections,
          date: null,
          time: null,
        }));
      }
    }

    return [
      {
        id: "group-1",
        selections: legacySelections,
        date: preselectedDate || null,
        time: preselectedTime || null,
      },
    ];
  })();
  const [bookingGroups, setBookingGroups] = useState(initialBookingGroups);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const activeGroup = bookingGroups[activeGroupIndex] || initialBookingGroups[0];
  const comboSelections = useMemo(
    () => activeGroup?.selections || [],
    [activeGroup]
  );
  const selectedServiceIds = useMemo(
    () => [
      ...new Set(
        bookingGroups
          .flatMap((group) => group.selections || [])
          .map((selection) => selection.serviceId)
      ),
    ],
    [bookingGroups]
  );

  const [services, setServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const currentDate = activeGroup?.date
      ? getMexicoDateTime(activeGroup.date).startOf("day")
      : getMexicoNow().startOf("day");

    return isValidDayjsDate(currentDate)
      ? currentDate
      : getMexicoNow().startOf("day");
  });
  const [selectedTime, setSelectedTime] = useState(
    activeGroup?.time ? { time: activeGroup.time, available: true } : null
  );
  const [horarios, setHorarios] = useState([]);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [punctualityOpen, setPunctualityOpen] = useState(false);
  const [confirmingBooking, setConfirmingBooking] = useState(false);
  const [loading, setLoading] = useState(true);
  const serviceAssignments = comboSelections.map((selection) => {
    const service = services.find((item) => Number(item.id) === selection.serviceId);

    return {
      ...selection,
      service,
    };
  });
  const bookingGroupSummaries = bookingGroups.map((group, index) => {
    const assignments = (group.selections || []).map((selection) => {
      const service = services.find((item) => Number(item.id) === selection.serviceId);

      return {
        ...selection,
        service,
        employee: service?.employees?.find((item) => Number(item.id) === Number(selection.employeeId)),
      };
    });
    const groupServices = assignments.map((assignment) => assignment.service).filter(Boolean);

    return {
      id: group.id || `group-${index + 1}`,
      assignments,
      date: group.date,
      time: group.time,
      amount: groupServices.reduce(
        (total, service) => total + Number(service?.price || 0),
        0
      ),
      duration: calculateCombinedReservationDuration(groupServices, assignments),
    };
  });
  const totalAmount = bookingGroupSummaries.reduce(
    (sum, group) => sum + Number(group.amount || 0),
    0
  );
  const totalDuration = bookingGroupSummaries.reduce(
    (sum, group) => sum + Number(group.duration || 0),
    0
  );
  const serviceNames = serviceAssignments
    .map((assignment) => assignment.service?.name)
    .filter(Boolean)
    .join(", ");
  const allGroupsScheduled = bookingGroups.every((group) => group.date && group.time);
  const compactSelectionsForActiveGroup = comboSelections
    .map((selection) => `${selection.serviceId}:${selection.employeeId}`)
    .join(",");

  useEffect(() => {
    const nextDate = activeGroup?.date
      ? getMexicoDateTime(activeGroup.date).startOf("day")
      : getMexicoNow().startOf("day");

    setSelectedDate(
      isValidDayjsDate(nextDate) ? nextDate : getMexicoNow().startOf("day")
    );
    setSelectedTime(activeGroup?.time ? { time: activeGroup.time, available: true } : null);
  }, [activeGroup?.date, activeGroupIndex]);

  const fetchServices = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/services`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || "Error obteniendo servicios");
      }

      const allServices = Array.isArray(data) ? data : [];
      const servicesById = new Map(
        allServices.map((item) => [Number(item.id), item])
      );

      setServices(selectedServiceIds.map((id) => servicesById.get(id)).filter(Boolean));
    } catch (error) {
      console.error("Error obteniendo servicios:", error);
      setServices([]);
    }
  }, [selectedServiceIds]);

  const fetchTimeSlots = useCallback(async () => {
    setLoading(true);
    try {
      if (!isValidDayjsDate(selectedDate)) {
        setHorarios([]);
        return;
      }

      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        date: selectedDate.format("YYYY-MM-DD"),
      });
      params.set("comboSelections", compactSelectionsForActiveGroup);
      if (isReschedule && rescheduleBookingId) {
        params.set("rescheduleBookingId", rescheduleBookingId);
      }

      const res = await fetch(
        `${API_BASE}/timeSlot/available?${params.toString()}`,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        }
      );

      const data = await res.json();
      setHorarios(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error obteniendo horarios:", error);
      setHorarios([]);
    } finally {
      setLoading(false);
    }
  }, [
    selectedDate,
    compactSelectionsForActiveGroup,
    isReschedule,
    rescheduleBookingId,
  ]);

  const validateAvailabilityBeforeSubmit = async () => {
    const token = localStorage.getItem("token");

    for (let index = 0; index < bookingGroups.length; index += 1) {
      const group = bookingGroups[index];
      const groupSelections = (group?.selections || [])
        .map((selection) => `${selection.serviceId}:${selection.employeeId}`)
        .join(",");

      if (!group?.date || !group?.time || !groupSelections) {
        return {
          ok: false,
          message: "Completá fecha y horario para cada servicio agregado.",
        };
      }

      const params = new URLSearchParams({
        date: group.date,
        comboSelections: groupSelections,
      });

      if (isReschedule && rescheduleBookingId) {
        params.set("rescheduleBookingId", rescheduleBookingId);
      }

      const response = await fetch(
        `${API_BASE}/timeSlot/available?${params.toString()}`,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        }
      );
      const data = await response.json().catch(() => []);

      if (!response.ok) {
        const detailedEmployeeMessage = data?.employeeName
          ? `${data.employeeName} ya no tiene disponibilidad en ${group.date} a las ${group.time}.`
          : null;

        return {
          ok: false,
          message:
            detailedEmployeeMessage ||
            data?.message ||
            "No se pudo validar la disponibilidad.",
        };
      }

      const matchingSlot = Array.isArray(data)
        ? data.find((slot) => slot.time === group.time)
        : null;

      if (!matchingSlot?.available) {
        setActiveGroupIndex(index);
        setSelectedDate(getMexicoDateTime(group.date).startOf("day"));
        setSelectedTime(null);
        return {
          ok: false,
          message:
            "Uno de los servicios combinados ya no tiene disponibilidad en ese horario. Elegí otro horario antes de continuar.",
        };
      }
    }

    return { ok: true };
  };
  // ===============================
  // UTIL: bloquear horarios pasados
  // ===============================
  const isPastTimeSlot = useCallback((time) => {
    if (!isValidDayjsDate(selectedDate)) {
      return true;
    }

    const nowInMexico = getMexicoNow();
    const today = nowInMexico.startOf("day");
    const selected = selectedDate.startOf("day");

    if (!selected.isSame(today)) return false;

    const slotDateTime = getMexicoDateTime(
      selectedDate.format("YYYY-MM-DD"),
      time
    );

    return slotDateTime.isBefore(nowInMexico);
  }, [selectedDate]);

  const isOriginalReservationWindow = useCallback((time) => {
    if (!isReschedule || !preselectedDate || !preselectedTime || !preselectedDuration) {
      return false;
    }

    if (!isValidDayjsDate(selectedDate)) {
      return false;
    }

    if (selectedDate.format("YYYY-MM-DD") !== preselectedDate) {
      return false;
    }

    const slotStart = getMexicoDateTime(selectedDate.format("YYYY-MM-DD"), time);
    const originalStart = getMexicoDateTime(preselectedDate, preselectedTime);
    const originalEnd = originalStart.add(preselectedDuration, "minute");

    return (
      (slotStart.isSame(originalStart) || slotStart.isAfter(originalStart)) &&
      slotStart.isBefore(originalEnd)
    );
  }, [isReschedule, preselectedDate, preselectedTime, preselectedDuration, selectedDate]);

  // ===============================
  // TRAER SERVICIO
  // ===============================
  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // ===============================
  // TRAER HORARIOS
  // ===============================
  useEffect(() => {
    if (!isValidDayjsDate(selectedDate)) {
      setHorarios([]);
      setLoading(false);
      return;
    }

    fetchTimeSlots();
  }, [selectedDate, activeGroupIndex, fetchTimeSlots]);

  useEffect(() => {
    const socket = io(SOCKET_URL);

    const handleAvailabilityUpdate = (payload = {}) => {
      if (payload.date !== selectedDate.format("YYYY-MM-DD")) {
        return;
      }

      const updatedEmployees = Array.isArray(payload.employeeIds)
        ? payload.employeeIds.map((value) => Number(value))
        : [];
      const hasRelevantEmployee = comboSelections.some((selection) =>
        updatedEmployees.includes(Number(selection.employeeId))
      );

      if (hasRelevantEmployee) {
        fetchTimeSlots();
      }
    };

    socket.on("availability:updated", handleAvailabilityUpdate);

    const handleCatalogUpdate = () => {
      fetchServices();
      fetchTimeSlots();
    };

    const handleEmployeesUpdate = () => {
      fetchServices();
      fetchTimeSlots();
    };

    const handleBookingsUpdate = () => {
      fetchTimeSlots();
    };

    socket.on("catalog:updated", handleCatalogUpdate);
    socket.on("employees:updated", handleEmployeesUpdate);
    socket.on("bookings:updated", handleBookingsUpdate);

    return () => {
      socket.off("availability:updated", handleAvailabilityUpdate);
      socket.off("catalog:updated", handleCatalogUpdate);
      socket.off("employees:updated", handleEmployeesUpdate);
      socket.off("bookings:updated", handleBookingsUpdate);
      socket.disconnect();
    };
  }, [
    selectedDate,
    compactSelectionsForActiveGroup,
    activeGroupIndex,
    comboSelections,
    fetchServices,
    fetchTimeSlots,
  ]);

  useEffect(() => {
    if (!preselectedTime || !preselectedDate) {
      setSelectedTime(null);
      return;
    }

    if (selectedDate.format("YYYY-MM-DD") !== preselectedDate) {
      setSelectedTime(null);
      return;
    }

    setSelectedTime((currentValue) => {
      if (currentValue?.time === preselectedTime) {
        return currentValue;
      }

      const matchingSlot = horarios.find((slot) => slot.time === preselectedTime);
      return matchingSlot || null;
    });
  }, [selectedDate, horarios, preselectedDate, preselectedTime]);

  useEffect(() => {
    if (!selectedTime) {
      return;
    }

    const matchingSlot = horarios.find((slot) => slot.time === selectedTime.time);

    if (!matchingSlot) {
      setSelectedTime(null);
      return;
    }

    const disabled =
      !matchingSlot.available ||
      isPastTimeSlot(matchingSlot.time) ||
      isOriginalReservationWindow(matchingSlot.time);

    if (disabled) {
      setSelectedTime(null);
      return;
    }

    if (matchingSlot !== selectedTime) {
      setSelectedTime(matchingSlot);
    }
  }, [
    selectedTime,
    horarios,
    selectedDate,
    isReschedule,
    preselectedDate,
    preselectedTime,
    preselectedDuration,
    isOriginalReservationWindow,
    isPastTimeSlot,
  ]);

  // ===============================
  // CONFIRMAR RESERVA
  // ===============================
  const openBookingSummary = () => {
    if (authLoading) return;
    if (!user && isReschedule) { navigate("/iniciar-sesión"); return; }
    if (!user && !guestContact) {
      setSummaryOpen(false); setGuestContactOpen(true); return;
    }
    setSummaryOpen(true);
  };
const submitBooking = async () => {
  if (services.length === 0) return;

  if (isReschedule && !selectedTime) {
    return;
  }

  if (!allGroupsScheduled) {
    alert("Completá fecha y horario para cada servicio agregado.");
    return;
  }

  const availabilityCheck = await validateAvailabilityBeforeSubmit();
  if (!availabilityCheck.ok) {
    alert(availabilityCheck.message);
    return;
  }

  const token = user ? localStorage.getItem("token") : null;
  if (!user && isReschedule) {
    navigate("/iniciar-sesión"); return;
  }
  if (!user && !guestContact) {
    setGuestContactOpen(true);
    return "needs-contact";
  }

  try {
    const endpoint = isReschedule
      ? `${API_BASE}/booking/${rescheduleBookingId}/reschedule`
      : `${API_BASE}/booking${user ? "" : "/guest"}`;
    const method = isReschedule ? "PATCH" : "POST";

    const res = await fetch(
      endpoint,
      {
        method,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          serviceId,
          employeeId,
          date: selectedDate.format("YYYY-MM-DD"),
          time: selectedTime.time,
          comboSelections,
          ...(!user ? { customer: guestContact } : {}),
          bookingGroups: bookingGroups.map((group) => ({
            id: group.id,
            date: group.date,
            time: group.time,
            selections: group.selections,
          })),
        }),
      }
    );

    // OJO: Aquí es donde solía dar el error "Unexpected end of input" 
    // si el servidor respondía vacío.
    const data = await res.json();

    if (!res.ok) {
      // Si el servidor responde 401 (No autorizado) aunque hubiera un token viejo/inválido
      if (res.status === 401) {
        logout();
        if (isReschedule) { navigate("/iniciar-sesión"); return; }
        setGuestContact(null); setGuestContactOpen(true);
        return "needs-contact";
      }
      throw new Error(data.message || "Error al reservar");
    }

    if (isReschedule) {
      alert("Reserva reagendada");
      navigate("/mis-reservas");
      return;
    }

    if (data.paymentRequired === false) {
      if (fromCart) {
        clearCart();
      }
      alert(isReschedule ? "Reserva reagendada" : "Reserva confirmada");
      navigate(isReschedule ? "/mis-reservas" : "/reserva/realizada");
      return;
    }

    if (data.paymentRequired === true && data.init_point) {
      if (fromCart) {
        localStorage.setItem("pendingCartCheckout", "1");
      }
      window.location.href = data.init_point;
      return;
    }

    throw new Error("Respuesta inesperada del servidor");
  } catch (error) {
    console.error("Error creando reserva:", error);
    alert(error.message || "Error al crear la reserva");
  }
};
  const handleConfirmBooking = async () => {
    if (confirmingBooking) return;
    setConfirmingBooking(true);
    let outcome;
    try {
      outcome = await submitBooking();
    } finally {
      setConfirmingBooking(false);
      setPunctualityOpen(false);
      setSummaryOpen(outcome !== "needs-contact");
    }
  };
  const cancelPunctualityNotice = () => {
    if (confirmingBooking) return;
    setPunctualityOpen(false);
    setSummaryOpen(true);
  };
  return (
    <>
      <Navbar />
      <GuestBookingDialog open={guestContactOpen} value={guestContact} onClose={() => setGuestContactOpen(false)} onContinue={(contact) => {
        setGuestContact(contact); setGuestContactOpen(false); setSummaryOpen(true);
      }} />

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          backgroundImage: "radial-gradient(ellipse at 100% 0%, rgba(220, 190, 131, 0.22), transparent 42%), radial-gradient(ellipse at 0% 35%, rgba(249, 236, 208, 0.3), transparent 45%)",
        }}
      >
        <Box sx={{ flexGrow: 1, pt: { xs: 5, md: 7 }, pb: { xs: 5, md: 9 } }}>
          <Box sx={{ maxWidth: 1000, mx: "auto", px: { xs: 2, sm: 3 } }}>
            <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: { xs: "left", sm: "center" } }}>
            <Typography sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: "#87642c", backgroundColor: "#fff8e9", border: "1px solid #ecd9ad", borderRadius: 10, px: 1.5, py: 0.6, fontSize: 10, fontWeight: 800, letterSpacing: 1.6, textTransform: "uppercase", mb: 2 }}>
              <EventAvailableRoundedIcon sx={{ fontSize: 15 }} />
              {isReschedule ? "Actualizá tu visita" : "Tu momento, a un paso"}
            </Typography>
            <Typography variant="h3" sx={{ color: "#30291e", fontSize: { xs: "2.15rem", sm: "2.8rem", md: "3.2rem" }, fontWeight: 800, letterSpacing: "-1.5px", lineHeight: 1.15, mb: 1.5 }}>
              {isReschedule ? "Elegí tu nueva fecha" : "Un espacio reservado para vos"}
            </Typography>
            <Typography sx={{ color: "#7b7468", fontSize: { xs: 14, md: 16 }, lineHeight: 1.7 }}>
              Elegí la fecha y el horario que mejor se adapten a vos.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "center" }, alignItems: "center", flexWrap: "wrap", gap: 1.25, mt: 2.5 }}>
              {["Elegí la fecha", "Seleccioná el horario", "Confirmá tu reserva"].map((label, index) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#786b56", fontSize: 11, fontWeight: 600 }}>
                  <Box component="span" sx={{ width: 23, height: 23, display: "grid", placeItems: "center", borderRadius: "50%", backgroundColor: index === 2 ? "#f7f1e5" : "#8a6d3b", color: index === 2 ? "#867b68" : "#fff", fontSize: 10, fontWeight: 800 }}>{index + 1}</Box>
                  {label}
                </Box>
              ))}
            </Box>
            </Box>

            {!isReschedule && (
              <Paper
                sx={{
                  p: { xs: 2, md: 2.5 },
                  mb: 3,
                  bgcolor: "#fff",
                  border: "1px solid #ebe4d7",
                  borderRadius: 4,
                  boxShadow: "0 8px 30px rgba(80, 61, 29, 0.04)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ color: "#8a6d3b", fontWeight: 800, letterSpacing: 1.4 }}
                    >
                      Configurando servicio
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
                      Bloque {activeGroupIndex + 1} de {bookingGroupSummaries.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {serviceNames}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      minWidth: 72,
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      textAlign: "center",
                      bgcolor: "#fcf6e8",
                      border: "1px solid #ead9b4",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1.1 }}>
                      {activeGroupIndex + 1}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      activo
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
                  Elegí la fecha y el horario de este bloque antes de pasar al siguiente.
                </Typography>

                {bookingGroupSummaries.length > 1 && (
                  <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={activeGroupIndex === 0}
                      onClick={() => setActiveGroupIndex((value) => Math.max(0, value - 1))}
                      sx={{ minWidth: 150 }}
                    >
                      Servicio anterior
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={activeGroupIndex === bookingGroupSummaries.length - 1}
                      onClick={() =>
                        setActiveGroupIndex((value) =>
                          Math.min(bookingGroupSummaries.length - 1, value + 1)
                        )
                      }
                      sx={{ minWidth: 150 }}
                    >
                      Siguiente servicio
                    </Button>
                  </Box>
                )}
              </Paper>
            )}

         

            {/* ===================== */}
            {/* CALENDARIO */}
            {/* ===================== */}
            <Paper elevation={0} sx={{ p: { xs: 2.25, sm: 3, md: 4 }, borderRadius: { xs: 3, md: 4 }, backgroundColor: "#fff", border: "1px solid #ebe4d7", boxShadow: "0 18px 55px rgba(80, 61, 29, 0.07)" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
              <Box sx={{ display: "grid", placeItems: "center", width: 42, height: 42, borderRadius: 2.5, backgroundColor: "#f5ecd9", color: "#8a6d3b" }}><EventAvailableRoundedIcon fontSize="small" /></Box>
              <Box>
                <Typography sx={{ fontSize: { xs: 17, md: 19 }, fontWeight: 800, color: "#30291e" }}>Elegí la fecha de tu visita</Typography>
                <Typography sx={{ fontSize: 12, color: "#867b68", mt: 0.25 }}>Consultá los turnos disponibles para cada día.</Typography>
              </Box>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <DatePicker
                label="Seleccionar fecha"
                value={selectedDate}
                minDate={getMexicoNow()}
                onChange={(newDate) => {
                  const normalizedDate = isValidDayjsDate(newDate)
                    ? newDate.startOf("day")
                    : getMexicoNow().startOf("day");

                  setBookingGroups((currentGroups) =>
                    currentGroups.map((group, index) =>
                      index === activeGroupIndex
                        ? {
                            ...group,
                            date: normalizedDate.format("YYYY-MM-DD"),
                            time: null,
                          }
                        : group
                    )
                  );

                  if (isValidDayjsDate(newDate)) {
                    setSelectedDate(normalizedDate);
                    setSelectedTime(null);
                    return;
                  }

                  setSelectedDate(normalizedDate);
                  setSelectedTime(null);
                }}
                sx={{
                  mb: 4,
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5,
                    backgroundColor: "#fff",
                    "& fieldset": { borderColor: "#e9dfcd" },
                    "&:hover fieldset": { borderColor: "#b99a61" },
                  },
                }}
              />
            </LocalizationProvider>

            {/* ===================== */}
            {/* HORARIOS */}
            {/* ===================== */}
            <Typography variant="h5" sx={{ mb: 0.75, fontWeight: 800, fontSize: { xs: 18, md: 21 }, color: "#30291e" }}>
              Horarios disponibles
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Seleccioná uno de los turnos disponibles para la fecha elegida.
            </Typography>

            {loading ? (
              <Typography>Cargando horarios...</Typography>
            ) : horarios.length === 0 ? (
              <Typography>No hay horarios disponibles.</Typography>
            ) : (
              <Box sx={SLOT_GRID_STYLES}>
                {horarios.map((h) => {
                  const disabled =
                    !h.available ||
                    isPastTimeSlot(h.time) ||
                    isOriginalReservationWindow(h.time);

                  return (
                    <Button
                      key={h.time}
                      fullWidth
                      variant={
                        selectedTime?.time === h.time
                          ? "contained"
                          : "outlined"
                      }
                      color="inherit"
                      disabled={disabled || authLoading}
                      aria-label={`${h.time}${disabled ? " — No disponible" : ""}`}
                      onClick={() => {
                        setSelectedTime(h);
                        openBookingSummary();
                        setBookingGroups((currentGroups) =>
                          currentGroups.map((group, index) =>
                            index === activeGroupIndex
                              ? {
                                  ...group,
                                  date: selectedDate.format("YYYY-MM-DD"),
                                  time: h.time,
                                }
                              : group
                          )
                        );
                      }}
                      sx={{
                        ...SLOT_BUTTON_STYLES,
                        flexDirection: "column",
                        gap: 0.25,
                        "&.Mui-disabled": {
                          backgroundColor: "#fff5f5",
                          borderColor: "#f1cccc",
                          color: "#8c7777",
                        },
                        color: selectedTime?.time === h.time ? "#fff" : "#8a6d3b",
                        backgroundColor: selectedTime?.time === h.time ? "#8a6d3b" : "#fff",
                        borderColor: "#e9dfcd",
                        transition: "background-color 160ms, border-color 160ms, box-shadow 160ms",
                        boxShadow: selectedTime?.time === h.time ? "0 6px 14px rgba(138, 109, 59, 0.2)" : "none",
                        "&:hover": {
                          backgroundColor: selectedTime?.time === h.time ? "#70572f" : "#fcf6e8",
                          borderColor: "#b99a61",
                        },
                      }}
                    >
                      {disabled && (
                        <Typography
                          component="span"
                          sx={{ color: "#c62828", fontSize: "0.7rem", fontWeight: 800, lineHeight: 1.3 }}
                        >
                          No disponible
                        </Typography>
                      )}
                      <Box component="span">{h.time}</Box>
                    </Button>
                  );
                })}
              </Box>
            )}

            </Paper>
            {/* ===================== */}
            {/* CONFIRMACIÓN */}
            {/* ===================== */}
            {bookingGroupSummaries.length > 0 && (
              <>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={openBookingSummary}
                  disabled={authLoading}
                  sx={{ mt: 3, py: 1.4, borderRadius: 2.5, color: "#181816", borderColor: "#d8d3ca", textTransform: "none" }}
                >
                  Ver resumen de la reserva
                </Button>
                <Dialog
                  open={summaryOpen}
                  onClose={() => !confirmingBooking && setSummaryOpen(false)}
                  fullWidth
                  maxWidth="sm"
                  aria-labelledby="booking-summary-title"
                  slotProps={{
                    backdrop: { sx: { backgroundColor: "rgba(18, 18, 16, 0.56)", backdropFilter: "blur(5px)" } },
                    paper: { sx: { borderRadius: 4, m: 2, width: "calc(100% - 32px)", maxHeight: "calc(100dvh - 32px)", boxShadow: "0 28px 90px rgba(0,0,0,0.25)", overflow: "hidden" } },
                  }}
                >
                  <DialogTitle component="div" sx={{ position: "relative", p: { xs: 2.5, sm: 3 }, pr: 7, backgroundColor: "#f8f6f1", borderBottom: "1px solid #eae5db" }}>
                    <IconButton
                      aria-label="Cerrar resumen de la reserva"
                      disabled={confirmingBooking}
                      onClick={() => setSummaryOpen(false)}
                      sx={{ position: "absolute", top: 14, right: 14, width: 36, height: 36, color: "#514b40", backgroundColor: "#fff", border: "1px solid #e6e0d5", "&:hover": { backgroundColor: "#eee8dc" } }}
                    >
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ width: 44, height: 44, flexShrink: 0, display: "grid", placeItems: "center", borderRadius: 3, backgroundColor: "#eee5d3", color: "#8a6d3b" }}>
                        <EventAvailableRoundedIcon />
                      </Box>
                      <Box>
                        <Typography sx={{ color: "#8a6d3b", fontSize: 10, fontWeight: 800, letterSpacing: 1.5, mb: 0.5 }}>TU PRÓXIMA VISITA</Typography>
                        <Typography id="booking-summary-title" component="h2" sx={{ fontWeight: 800, fontSize: { xs: "1.2rem", sm: "1.4rem" }, lineHeight: 1.25, color: "#24231f" }}>Resumen de la reserva</Typography>
                      </Box>
                    </Box>
                  </DialogTitle>
                  <DialogContent sx={{ p: { xs: 2.5, sm: 3 }, "&.MuiDialogContent-root": { pt: 2.5 } }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
                    Revisá los servicios, profesionales y horarios antes de confirmar.
                  </Typography>
                  {!user && guestContact && (
                    <Box sx={{ mb: 2.5, p: 2, borderRadius: 2.5, backgroundColor: "#fcf8ef", border: "1px solid #eae1d0" }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "#8a6d3b", mb: 0.5 }}>DATOS DE CONTACTO</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{guestContact.name} {guestContact.lastName}</Typography>
                      <Typography sx={{ color: "#7b7468", fontSize: 13 }}>WhatsApp: {guestContact.phone}</Typography>
                      <Button size="small" onClick={() => { setSummaryOpen(false); setGuestContactOpen(true); }} sx={{ mt: 0.5, textTransform: "none", color: "#8a6d3b" }}>Editar datos</Button>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "1fr",
                      },
                      gap: 1.5,
                    }}
                  >
                    {bookingGroupSummaries.map((group, index) => (
                      <Box
                        key={group.id || `booking-summary-${index}`}
                        sx={{ ...SUMMARY_CARD_STYLES, p: 2.25, borderRadius: 3, backgroundColor: "#fff", borderColor: "#e8e5de" }}
                      >
                        <Typography sx={{ color: "#8a6d3b", fontSize: 10, fontWeight: 800, letterSpacing: 1, mb: 0.75 }}>SERVICIO {bookingGroupSummaries.length > 1 ? index + 1 : ""}</Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", mb: 0.75, color: "#24231f" }}>
                          {(group.assignments || [])
                            .map((assignment) => assignment.service?.name)
                            .filter(Boolean)
                            .join(", ")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Profesional: {(group.assignments || []).map((assignment) => assignment.employee?.name).filter(Boolean).join(", ") || "—"}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.75 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.8, backgroundColor: "#f7f5f0", borderRadius: 2, color: "#514b40", fontSize: 13 }}>
                            <EventAvailableRoundedIcon sx={{ fontSize: 17, color: "#8a6d3b" }} />
                            {group.date ? getMexicoDateTime(group.date).format("DD/MM/YYYY") : "Fecha pendiente"}
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.8, backgroundColor: "#f7f5f0", borderRadius: 2, color: "#514b40", fontSize: 13 }}>
                            <ScheduleRoundedIcon sx={{ fontSize: 17, color: "#8a6d3b" }} />
                            {group.time || "Hora pendiente"} · {group.duration} min
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1.75, pt: 1.5, borderTop: "1px solid #f0ede7", fontWeight: 700, textAlign: "right" }}>
                          Reserva: {money.format(group.amount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      mt: 2,
                      p: 2.5,
                      borderRadius: 3,
                      backgroundColor: "#24231f",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#e4dccb" }}>Total de la reserva</Typography>
                      <Typography sx={{ fontSize: 12, color: "#c4bfb3", mt: 0.5 }}>Duración total: {totalDuration} min</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.6rem", letterSpacing: -0.5 }}>{money.format(totalAmount)}</Typography>
                  </Box>

                  {!allGroupsScheduled && (
                    <Typography variant="body2" sx={{ mt: 2, color: "#9a641a" }}>
                      Falta elegir fecha y horario para los demás bloques de servicios.
                    </Typography>
                  )}
                  </DialogContent>
                  <DialogActions sx={{ p: { xs: 2.5, sm: 3 }, borderTop: "1px solid #eae5db", backgroundColor: "#faf9f6", flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1.25, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
                  <Button disabled={confirmingBooking} onClick={() => setSummaryOpen(false)} sx={{ textTransform: "none", color: "#555", py: 1.25, px: 2, width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}>
                    Cambiar horario
                  </Button>
                  {!allGroupsScheduled ? (
                    <Button variant="contained" onClick={() => {
                      const nextIndex = bookingGroups.findIndex((group) => !group.date || !group.time);
                      if (nextIndex >= 0) setActiveGroupIndex(nextIndex);
                      setSummaryOpen(false);
                    }} sx={{ backgroundColor: "#181816", textTransform: "none", borderRadius: 2.5, py: 1.35, flex: 1, width: { xs: "100%", sm: "auto" } }}>
                      Elegir siguiente horario
                    </Button>
                  ) : <Button
                    sx={{
                      flex: 1,
                      width: { xs: "100%", sm: "auto" },
                      px: 2.5,
                      py: 1.4,
                      borderRadius: 2.5,
                      backgroundColor: "#181816",
                      textTransform: "none",
                      fontWeight: 750,
                      "&:hover": { backgroundColor: "#343430" },
                    }}
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setSummaryOpen(false);
                      setPunctualityOpen(true);
                    }}
                    disabled={!allGroupsScheduled || confirmingBooking}
                  >
                    {confirmingBooking ? "Confirmando..." : isReschedule ? "Confirmar reagendado" : "Confirmar reserva"}
                  </Button>}
                  </DialogActions>
                </Dialog>

                <Dialog
                  open={punctualityOpen}
                  onClose={cancelPunctualityNotice}
                  fullWidth
                  maxWidth="xs"
                  aria-labelledby="punctuality-notice-title"
                  aria-describedby="punctuality-notice-description"
                  slotProps={{
                    backdrop: { sx: { backgroundColor: "rgba(18, 18, 16, 0.56)", backdropFilter: "blur(5px)" } },
                    paper: { sx: { borderRadius: 4, m: 2, width: "calc(100% - 32px)", boxShadow: "0 28px 90px rgba(0,0,0,0.25)" } },
                  }}
                >
                  <DialogTitle component="div" sx={{ p: 3, pr: 6, backgroundColor: "#fff8e9", borderBottom: "1px solid #ecd9ad", position: "relative" }}>
                    <IconButton aria-label="Cerrar aviso y volver al resumen" disabled={confirmingBooking} onClick={cancelPunctualityNotice} sx={{ position: "absolute", right: 12, top: 12, color: "#8a6d3b" }}>
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                    <InfoOutlinedIcon sx={{ color: "#8a6d3b", fontSize: 30, mb: 1 }} />
                    <Typography id="punctuality-notice-title" component="h2" sx={{ color: "#70572f", fontSize: 19, fontWeight: 800, lineHeight: 1.4 }}>
                      Importante: Agradecemos su puntualidad
                    </Typography>
                  </DialogTitle>
                  <DialogContent sx={{ p: 3, "&.MuiDialogContent-root": { pt: 3 } }}>
                    <Typography id="punctuality-notice-description" sx={{ color: "#6f6250", fontSize: 15, lineHeight: 1.8 }}>
                      Es importante llegar a la hora seleccionada, no contamos con tiempo de espera, respetando asi el tiempo de los demas clientes y continuidad de las citas.
                    </Typography>
                  </DialogContent>
                  <DialogActions sx={{ px: 3, pb: 3, gap: 1, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
                    <Button disabled={confirmingBooking} onClick={cancelPunctualityNotice} variant="outlined" sx={{ flex: 1, py: 1.25, borderRadius: 2.5, textTransform: "none", color: "#70572f", borderColor: "#d9c7a5" }}>
                      Cancelar
                    </Button>
                    <Button disabled={!allGroupsScheduled || confirmingBooking} onClick={handleConfirmBooking} variant="contained" disableElevation sx={{ flex: 1, py: 1.25, borderRadius: 2.5, textTransform: "none", fontWeight: 750, backgroundColor: "#8a6d3b", "&:hover": { backgroundColor: "#70572f" } }}>
                      {confirmingBooking ? "Confirmando..." : "Continuar"}
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* ===================== */}
                {/* AVISO IMPORTANTE */}
                {/* ===================== */}
                <Paper
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: "rgba(255, 255, 255, 0.85)",
                    border: "1px solid #ebe4d7",
                    boxShadow: "none",
                  }}
                >
                  <Typography fontWeight="bold" sx={{ mb: 1 }}>
                    Importante
                  </Typography>

                  <Typography sx={{ fontSize: 14 }}>
                    Para cancelar una reservación es necesario comunicarse
                    directamente con la tienda.
                  </Typography>

                  <Typography sx={{ fontSize: 14, mt: 1 }}>
                    La cancelación debe realizarse dentro de las{" "}
                    <strong>24 horas</strong>. Pasado ese plazo,{" "}
                    <strong>no se realizará la devolución</strong> del monto
                    abonado por la reserva.
                  </Typography>
                </Paper>
              </>
            )}
          </Box>
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default ReservaPage;
